/**
 * User Media Helper
 * Wrapper functions for navigator.mediaDevices API with confirmation dialogs
 */
import { CONFIRM_NOTIFICATION_SIZE, CONFIRM_NOTIFICATION_THEME } from "../../Modals/Notifications/ConfirmNotification";
import { ParticipantMode } from "../../Navbar/Navbar";

const CONFIRMATION_TITLE = "You need to activate your devices";
const CONFIRMATION_MESSAGE = "For interaction with others your camera and microphone need to be activated.";

// Store permission state to avoid repeated checks
let permissionState = {
  camera: 'unknown',
  microphone: 'unknown'
};

/**
 * Check if the user has already granted permissions for media devices
 * This method will NOT trigger any browser permission dialogs
 * @param {string} mediaType - Type of media to check ('camera', 'microphone', or 'all')
 * @returns {Promise<boolean>} - True if permissions are granted, false otherwise
 */
export const checkMediaPermissions = async (mediaType = 'all') => {
  try {
    // If we have a cached 'granted' state, use it
    if (mediaType === 'camera' && permissionState.camera === 'granted') {
      return true;
    }
    if (mediaType === 'microphone' && permissionState.microphone === 'granted') {
      return true;
    }
    if (mediaType === 'all' &&
      permissionState.camera === 'granted' &&
      permissionState.microphone === 'granted') {
      return true;
    }

    // Use navigator.mediaDevices.enumerateDevices() to check permissions without prompting
    // This will only return detailed device info if permissions are granted
    const devices = await navigator.mediaDevices.enumerateDevices();

    // Check if we have detailed device information (indicates permission granted)
    const videoDevices = devices.filter(device => device.kind === 'videoinput' && device.label);
    const audioDevices = devices.filter(device => device.kind === 'audioinput' && device.label);

    // Update our cached permission state
    permissionState.camera = videoDevices.length > 0 ? 'granted' : 'prompt';
    permissionState.microphone = audioDevices.length > 0 ? 'granted' : 'prompt';

    // Return based on requested media type
    if (mediaType === 'camera') {
      return permissionState.camera === 'granted';
    } else if (mediaType === 'microphone') {
      return permissionState.microphone === 'granted';
    } else {
      // For 'all', both need to be granted
      return permissionState.camera === 'granted' && permissionState.microphone === 'granted';
    }
  } catch (error) {
    console.error('Error checking media permissions:', error);
    return false;
  }
};

/**
 * Update permission state after a successful media access
 * @param {MediaStream} stream - The media stream that was granted
 */
const updatePermissionState = (stream) => {
  if (!stream) return;

  const tracks = stream.getTracks();
  tracks.forEach(track => {
    if (track.kind === 'video') {
      permissionState.camera = 'granted';
    } else if (track.kind === 'audio') {
      permissionState.microphone = 'granted';
    }
  });
};


const showMediaPermissionDialog = async (model = null) => {
  if (!model) {
    return window.confirm(CONFIRMATION_MESSAGE);
  }

  return await new Promise(async (resolve, reject) => {
    model.confirm(
      CONFIRMATION_MESSAGE,
      () => resolve(true),
      {
        header: true,
        title: CONFIRMATION_TITLE,
        confirmBtnTxt: "Activate Devices",
        cancelBtnTxt: "Cancel",
        theme: CONFIRM_NOTIFICATION_THEME.DARK,
        size: CONFIRM_NOTIFICATION_SIZE.SM,
      },
      () => resolve(false)
    )
  })
};

/**
 * Request media permissions with confirmation
 * This function will show a confirmation dialog and then request permissions if confirmed
 * @param {string} mediaType - Type of media to request ('camera', 'microphone', or 'all')
 * @param {Model} model - view model
 * @returns {Promise<boolean>} - True if confirmed and permissions granted, false otherwise
 */
export const requestMediaPermissions = async (mediaType = 'all', model = null) => {
  try {
    // First check if permissions are already granted
    const permissionsGranted = await checkMediaPermissions(mediaType);
    if (permissionsGranted) {
      return true;
    }

    // Show confirmation dialog
    const confirmed = await showMediaPermissionDialog(model);
    if (!confirmed) {
      return false;
    }

    // Request permissions based on media type
    let constraints = {};
    if (mediaType === 'camera' || mediaType === 'all') {
      constraints.video = true;
    }
    if (mediaType === 'microphone' || mediaType === 'all') {
      constraints.audio = true;
    }

    // Request permissions
    const stream = await navigator.mediaDevices.getUserMedia(constraints);

    // Update permission state
    updatePermissionState(stream);

    // Stop all tracks to release devices
    stream.getTracks().forEach(track => track.stop());

    // Hide activity button
    ParticipantMode.hideButton()

    return true;
  } catch (error) {
    localStorage.setItem('mediapermissoion', 'notset');
    console.error('Error requesting media permissions:', error);
    return false;
  }
};

/**
 * Wrapper for navigator.mediaDevices.getUserMedia with permission check
 * @param {MediaStreamConstraints} constraints - Media stream constraints
 * @param {Model} model - view model
 * @returns {Promise<MediaStream>} - Media stream or null if rejected
 */
export const getUserMedia = async (constraints, model = null) => {
  try {
    // Determine which permissions to check based on constraints
    let mediaTypeToCheck = 'all';
    if (constraints) {
      if (constraints.video && !constraints.audio) {
        mediaTypeToCheck = 'camera';
      } else if (constraints.audio && !constraints.video) {
        mediaTypeToCheck = 'microphone';
      }
    }

    // Check if permissions are already granted
    const permissionsGranted = await checkMediaPermissions(mediaTypeToCheck);

    // If permissions are not granted, show confirmation
    if (!permissionsGranted) {
      const confirmed = await showMediaPermissionDialog(model);
      if (!confirmed) {
        return null;
      }
    }

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    updatePermissionState(stream);
    return stream;
  } catch (error) {
    console.error('Error accessing media devices:', error);
    throw error;
  }
};

/**
 * Wrapper for navigator.mediaDevices.getDisplayMedia with permission check
 * @param {DisplayMediaStreamConstraints} options - Display media options
 * @param {Model} model - view model
 * @returns {Promise<MediaStream>} - Media stream or null if rejected
 */
export const getDisplayMedia = async (options = {}, model = null) => {
  try {
    // For screen sharing, always check if audio is requested
    const needsAudioPermission = options.audio !== undefined && options.audio !== false;
    const permissionsGranted = needsAudioPermission ?
      await checkMediaPermissions('microphone') : true;

    // If audio permissions are not granted and needed, show confirmation
    if (!permissionsGranted) {
      const confirmed = await showMediaPermissionDialog(model);
      if (!confirmed) {
        return null;
      }
    }

    const stream = await navigator.mediaDevices.getDisplayMedia(options);

    updatePermissionState(stream);

    return stream;
  } catch (error) {
    console.error('Error accessing display media:', error);
    throw error;
  }
};

/**
 * Wrapper for navigator.mediaDevices.enumerateDevices with permission check
 * @param {Model} model - view model
 * @returns {Promise<MediaDeviceInfo[]>} - List of media devices or empty array if rejected
 */
export const enumerateDevices = async (model = null) => {
  try {
    // Check if any media permissions are already granted
    const permissionsGranted = await checkMediaPermissions('all');

    // If permissions are not granted, show confirmation
    if (!permissionsGranted) {
      const confirmed = await showMediaPermissionDialog(model);
      if (!confirmed) {
        return [];
      }
    }

    return await navigator.mediaDevices.enumerateDevices();
  } catch (error) {
    console.error('Error enumerating devices:', error);
    throw error;
  }
};

/**
 * Check if the browser supports mediaDevices API
 * @returns {boolean} - True if supported, false otherwise
 */
export const isMediaDevicesSupported = () => {
  return !!(navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia &&
    navigator.mediaDevices.getDisplayMedia &&
    navigator.mediaDevices.enumerateDevices);
};
